import { z } from 'zod'
import type { ApiClient } from './client'
import { ApiError } from './client'
import { ackEnvelope, assertAck } from './envelope'

/**
 * Extractors — POST /{clusterId}/api/parsers/list
 *
 * The path says "parsers"; the domain (and every field in the payload) says
 * EXTRACTORS — extractor_id, extractor_type. Use "extractor" everywhere in the
 * console; the path is legacy naming and stays confined to this file.
 *
 * Shape captured live from the gotham scope of the ap1 tenant on 2026-08-21
 * (217 extractors). What the capture established:
 *
 * - `data` is a BARE ARRAY, like daily-stream-details and unlike the mgmt list
 *   endpoints. `count` is the full total and pagination IS honoured here
 *   (pagesize=5 returned 5), unlike stream-management.
 * - Casing is inconsistent and must be matched exactly: `status` is UPPERCASE
 *   ('ENABLED' 205, 'DISABLED' 12) while `publish_status` is lowercase
 *   ('published' 213, 'draft' 4).
 * - `update_tstamp` is a STRING holding epoch seconds, sometimes with a
 *   fractional part ('1787229677', '1779347413.0201242').
 * - `validation_msg` is NOT always a message. It is either the plain string
 *   'Success', or null, or a JSON document. See parseValidationDetail.
 * - `validation_stage` can be null. 212 of 217 were 'Integrator Success'; the
 *   remaining 5 are the ones worth a human's attention, and they are exactly
 *   the rows carrying a JSON validation_msg.
 */

export const extractor = z
  .object({
    extractor_id: z.string(),
    entity_id: z.string().optional(),
    name: z.string(),
    /** UPPERCASE: 'ENABLED' | 'DISABLED'. Kept permissive. */
    status: z.string().default(''),
    /** lowercase: 'published' | 'draft'. Note the casing differs from status. */
    publish_status: z.string().default(''),
    /** 'native' | 'custom'. */
    extractor_type: z.string().default(''),
    source_name: z.string().nullable().default(null),
    source_type: z.string().nullable().default(null),
    author: z.string().nullable().default(null),
    version: z.string().nullable().default(null),
    validation_stage: z.string().nullable().default(null),
    validation_msg: z.string().nullable().default(null),
    /** Epoch seconds, as a string. */
    update_tstamp: z.string().nullable().default(null),
  })
  .passthrough()

export const extractorsResponse = z.object({
  count: z.number().default(0),
  message: z.string().optional(),
  status: z.string().optional(),
  TStamp: z.string().optional(),
  data: z.array(extractor).default([]),
})

export type Extractor = z.output<typeof extractor>

export interface ExtractorsPage {
  extractors: Extractor[]
  /** Total across all pages. */
  total: number
}

export interface ExtractorsQuery {
  clusterId: string
  scopeId: string
  pageNo?: number
  pageSize?: number
}

/* ------------------------------------------------- write-path contracts */
/*
 * Everything below was captured live on 2026-09-08 (WAYNE-CORP/gotham) —
 * docs/inventory/extractors-write-capture.md. The identity model matters:
 *
 *   native row     extractor_id "730"            author "Bloo"
 *   custom twin    extractor_id "730_gotham"     one per native per scope
 *   draft          extractor_id "730_gotham_draft"
 *
 * Enable and disable are ASYMMETRIC on the wire. Disable is a small
 * changestatus call; enable is wrk/api/parsers/save with call_type STATE and
 * needs the full YAML + log sample, and it re-validates and re-publishes
 * (backend ask CB-24). So `enable` here reads `details` first.
 */

/** `POST /api/parsers/listparserdetails` — YAML and samples arrive base64. */
export const extractorDetailsResponse = ackEnvelope.extend({
  data: z.object({
    'extractor-details': z.string(),
    logevents: z.string().nullable().default(null),
  }),
})

export interface ExtractorDetails {
  /** The extractor YAML, decoded. */
  yaml: string
  /** The saved log samples, decoded (one string per sample). */
  logevents: string[]
  /** The samples joined, which is what the legacy editor sends back on save. */
  logevent: string
}

/** `POST /wrk/api/parsers/status` — the row plus audit fields. */
export const extractorStatusRow = extractor
  .extend({
    scope_id: z.string().optional(),
    created_by: z.string().nullable().optional(),
    updated_by: z.string().nullable().optional(),
    create_tstamp: z.string().nullable().optional(),
  })
  .passthrough()
export const extractorStatusResponse = ackEnvelope.extend({ data: extractorStatusRow })
export type ExtractorStatus = z.output<typeof extractorStatusRow>

/**
 * `POST /api/parsers/top` — `data` is an OBJECT keyed by rank ("1"…"5"), not an
 * array (backend ask CB-27). Normalised to an array here, rank order kept.
 */
export const topExtractorRaw = z.object({ ID: z.string(), Name: z.string(), PAD: z.number() }).passthrough()
export const topExtractorsResponse = ackEnvelope.extend({ data: z.record(z.string(), topExtractorRaw) })
export interface TopExtractor {
  rank: number
  extractorId: string
  name: string
  /** Parsed events in the window (the payload calls it PAD). */
  events: number
}

/**
 * `POST /api/parsers/play` — simulate YAML over samples. Rows are bare
 * PascalCase events (`Stream`, `Action`, `User`, `SrcIP`, `LogEvent`,
 * `ExtractorID` (a NUMBER here), `PStatus: "PAD"`, `EvtLen`…), one per sample
 * line the extractor matched; `count` is the total across pages.
 */
export const playRow = z.record(z.string(), z.unknown())
export const playResponse = ackEnvelope.extend({
  count: z.number().default(0),
  data: z.array(playRow).default([]),
})
export type PlayRow = z.output<typeof playRow>
export interface PlayResult {
  rows: PlayRow[]
  total: number
}

/**
 * `POST /wrk/api/parsers/save` — captured 2026-09-08. `call_type: "EDIT"`
 * lands as a DRAFT (`data: {Status: "parser saved to draft", extractor_id:
 * "<id>_draft"}`); `call_type: "STATE"` with `status: "ENABLED"` is the
 * publish/enable path (re-validates, re-publishes; 500 on a draft id). The
 * body must carry the YAML and the joined samples in both cases.
 */
export const saveResponse = ackEnvelope.extend({
  data: z.union([
    z.object({ Status: z.string().optional(), extractor_id: z.string().optional() }).passthrough(),
    z.string(),
    z.null(),
  ]).optional(),
})
export interface SaveResult {
  /** The id the backend wrote — `<id>_draft` for EDIT; the row id (or nothing) for STATE. */
  extractorId: string | null
  status: string | null
}

/**
 * `POST /wrk/api/parsers/add` — captured 2026-09-08 as the CLONE path
 * (`progenitor_id` = the native's bare id): answers
 * `data: {extractor_id: "<native>_<scope>_draft", extractor_name}` and, as
 * side effects, creates the published twin `<native>_<scope>` and DISABLES
 * the native (CB-25). Without `progenitor_id` it is the legacy "create from
 * scratch" (the legacy body also carried GenAI analysis payloads; that
 * variant is UNTESTED without them — requirements W4).
 */
export const addResponse = ackEnvelope.extend({
  data: z.object({ extractor_id: z.string(), extractor_name: z.string().optional() }).passthrough(),
})
export interface AddResult {
  /** The draft id the backend wrote (`<id>_<scope>_draft`). */
  draftId: string
  /** The published row id (`<id>_<scope>`) — what the workbench opens. */
  extractorId: string
  name: string
}

const deleteResponse = ackEnvelope.extend({
  data: z.object({ extractor_id: z.string() }).passthrough().optional(),
})

export interface ExtractorScope {
  clusterId: string
  scopeId: string
}

export interface ExtractorsApi {
  list: (q: ExtractorsQuery) => Promise<ExtractorsPage>
  /** YAML + samples for one row. 400 for a `_draft` id (backend ask CB-29). */
  details: (q: ExtractorScope & { extractorId: string }) => Promise<ExtractorDetails>
  /** The row as the backend sees it now — the poll target after any write. */
  status: (q: ExtractorScope & { extractorId: string }) => Promise<ExtractorStatus>
  /** `parsers/changestatus` — the DISABLE path. */
  disable: (q: ExtractorScope & { extractorId: string }) => Promise<void>
  /**
   * The ENABLE path: `wrk/api/parsers/save` with `call_type: 'STATE'`. Reads
   * `details` first because the body must carry the YAML and the sample. The
   * backend re-validates and re-publishes on success.
   */
  enable: (q: ExtractorScope & { extractorId: string; name: string }) => Promise<void>
  /** `parsers/delete` — removes ONLY the id given; a twin and its draft are two calls. */
  remove: (q: ExtractorScope & { extractorId: string; extractorType: string }) => Promise<void>
  /** `parsers/top` — ranked by parsed events over the window (e.g. '24h'). */
  top: (q: ExtractorScope & { duration?: string }) => Promise<TopExtractor[]>
  /**
   * `wrk/api/parsers/save` with an explicit body. EDIT = save as draft;
   * STATE = set `status` (ENABLED publishes). `enable` above is the STATE
   * path with the stored YAML; this one takes the editor's text.
   */
  save: (q: ExtractorScope & { extractorId: string; name: string; yaml: string; logevent: string; status: 'ENABLED' | 'DISABLED'; callType: 'EDIT' | 'STATE' }) => Promise<SaveResult>
  /** `wrk/api/parsers/add` — clone (with `progenitorId`) or create from scratch. See the side effects above. */
  add: (q: ExtractorScope & { name: string; yaml: string; logevent: string; status: 'ENABLED' | 'DISABLED'; progenitorId?: string }) => Promise<AddResult>
  /** `parsers/play` — pure compute: run `yaml` over `logevent` (samples joined), paged. */
  play: (q: ExtractorScope & { yaml: string; logevent: string; pageNo?: number; pageSize?: number }) => Promise<PlayResult>
}

/** Base64 → UTF-8 text. `atob` is a browser global and a Node 16+ global. */
function decodeBase64(s: string): string {
  const bin = atob(s)
  const bytes = Uint8Array.from(bin, (c) => c.charCodeAt(0))
  return new TextDecoder().decode(bytes)
}

export function decodeExtractorDetails(raw: z.output<typeof extractorDetailsResponse>['data']): ExtractorDetails {
  const yaml = decodeBase64(raw['extractor-details'])
  let logevents: string[] = []
  if (raw.logevents) {
    try {
      const parsed: unknown = JSON.parse(decodeBase64(raw.logevents))
      logevents = Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === 'string') : []
    } catch {
      logevents = []
    }
  }
  return { yaml, logevents, logevent: logevents.join('') }
}

export function normaliseTop(data: Record<string, z.output<typeof topExtractorRaw>>): TopExtractor[] {
  return Object.entries(data)
    .map(([rank, v]) => ({ rank: Number(rank), extractorId: v.ID, name: v.Name, events: v.PAD }))
    .filter((t) => Number.isFinite(t.rank))
    .sort((a, b) => a.rank - b.rank)
}

export function createExtractorsApi(client: ApiClient): ExtractorsApi {
  const details: ExtractorsApi['details'] = async ({ clusterId, extractorId }) => {
    const res = await client.request('POST', `/${clusterId}/api/parsers/listparserdetails`, {
      body: { extractor_id: extractorId },
      schema: extractorDetailsResponse,
    })
    assertAck(res, 'Reading the extractor')
    return decodeExtractorDetails(res.data)
  }
  return {
    list: async ({ clusterId, scopeId, pageNo = 1, pageSize = 1000 }) => {
      const res = await client.request('POST', `/${clusterId}/api/parsers/list`, {
        body: { scope_id: scopeId, pageno: pageNo, pagesize: pageSize },
        schema: extractorsResponse,
      })
      return { extractors: res.data, total: res.count }
    },
    details,
    status: async ({ clusterId, scopeId, extractorId }) => {
      // Right after a write the backend answers HTTP 310 + status "failed"
      // while the validator runs (CB-26); the client throws on !ok, and the
      // caller treats that as "not settled yet".
      const res = await client.request('POST', `/${clusterId}/wrk/api/parsers/status`, {
        body: { scope_id: scopeId, extractor_id: extractorId },
        schema: extractorStatusResponse,
      })
      assertAck(res, 'Reading the extractor status')
      return res.data
    },
    disable: async ({ clusterId, scopeId, extractorId }) => {
      const res = await client.request('POST', `/${clusterId}/api/parsers/changestatus`, {
        body: { status: 'DISABLED', scope_id: scopeId, extractor_id: extractorId },
        schema: ackEnvelope,
      })
      assertAck(res, 'Disabling the extractor')
    },
    enable: async ({ clusterId, scopeId, extractorId, name }) => {
      const d = await details({ clusterId, scopeId, extractorId })
      if (!d.yaml) throw new ApiError(400, 'The extractor has no YAML to publish.', null)
      const res = await client.request('POST', `/${clusterId}/wrk/api/parsers/save`, {
        body: {
          scope_id: scopeId,
          extractor_id: extractorId,
          extractor_details: d.yaml,
          parser_name: name,
          status: 'ENABLED',
          logevent: d.logevent,
          call_type: 'STATE',
        },
        schema: ackEnvelope,
      })
      assertAck(res, 'Enabling the extractor')
    },
    remove: async ({ clusterId, scopeId, extractorId, extractorType }) => {
      const res = await client.request('POST', `/${clusterId}/api/parsers/delete`, {
        body: { scope_id: scopeId, extractor_id: extractorId, extractor_type: extractorType },
        schema: deleteResponse,
      })
      assertAck(res, 'Deleting the extractor')
    },
    save: async ({ clusterId, scopeId, extractorId, name, yaml, logevent, status, callType }) => {
      const res = await client.request('POST', `/${clusterId}/wrk/api/parsers/save`, {
        body: {
          scope_id: scopeId,
          extractor_id: extractorId,
          extractor_details: yaml,
          parser_name: name,
          status,
          logevent,
          call_type: callType,
        },
        schema: saveResponse,
      })
      assertAck(res, callType === 'EDIT' ? 'Saving the draft' : 'Publishing the extractor')
      const d = res.data
      return typeof d === 'object' && d !== null
        ? { extractorId: d.extractor_id ?? null, status: d.Status ?? null }
        : { extractorId: null, status: typeof d === 'string' && d ? d : null }
    },
    add: async ({ clusterId, scopeId, name, yaml, logevent, status, progenitorId }) => {
      const res = await client.request('POST', `/${clusterId}/wrk/api/parsers/add`, {
        body: {
          scope_id: scopeId,
          status,
          name,
          data: yaml,
          logevent,
          ...(progenitorId ? { progenitor_id: progenitorId } : {}),
        },
        schema: addResponse,
      })
      assertAck(res, progenitorId ? 'Cloning the extractor' : 'Creating the extractor')
      const draftId = res.data.extractor_id
      return { draftId, extractorId: draftId.replace(/_draft$/, ''), name: res.data.extractor_name ?? name }
    },
    play: async ({ clusterId, scopeId, yaml, logevent, pageNo = 1, pageSize = 50 }) => {
      const res = await client.request('POST', `/${clusterId}/api/parsers/play`, {
        body: { scope_id: scopeId, data: yaml, logevent, pageno: pageNo, pagesize: pageSize },
        schema: playResponse,
      })
      assertAck(res, 'Simulating the extractor')
      return { rows: res.data, total: res.count }
    },
    top: async ({ clusterId, scopeId, duration = '24h' }) => {
      const res = await client.request('POST', `/${clusterId}/api/parsers/top`, {
        body: { scope_id: scopeId, duration },
        schema: topExtractorsResponse,
      })
      assertAck(res, 'Reading top extractors')
      return normaliseTop(res.data)
    },
  }
}

/* ------------------------------------------------------------ identity */

/** `"730_gotham_draft"` → true. Drafts cannot be read back (CB-29). */
export function isDraftId(extractorId: string): boolean {
  return extractorId.endsWith('_draft')
}

/**
 * The rows `parsers/delete` must be called for to fully remove a custom
 * extractor: the id given plus its `_draft` sibling when the list carries one
 * (a clone creates both; delete removes only the id it is given — CB-25).
 */
export function deletionTargets(e: Extractor, all: Extractor[]): Extractor[] {
  const base = e.extractor_id.replace(/_draft$/, '')
  const ids = new Set([base, `${base}_draft`])
  return all.filter((x) => ids.has(x.extractor_id))
}

/** The native a custom twin (`"730_gotham"`) was cloned from, if listed. */
export function nativeOf(e: Extractor, all: Extractor[]): Extractor | null {
  if (!isCustom(e)) return null
  const nativeId = e.extractor_id.split('_')[0] ?? ''
  return all.find((x) => x.extractor_id === nativeId && !isCustom(x)) ?? null
}

/* ----------------------------------------------------------------- helpers */

export const SUCCESS_STAGE = 'Integrator Success'

/**
 * An extractor a human should look at: validation did not land on success.
 * In the capture these 5 of 217 are exactly the rows whose validation_msg
 * carries a JSON document rather than the word "Success".
 */
export function needsAttention(e: Extractor): boolean {
  return e.validation_stage !== SUCCESS_STAGE
}

export function isEnabled(e: Extractor): boolean {
  return e.status.toUpperCase() === 'ENABLED'
}

export function isCustom(e: Extractor): boolean {
  return e.extractor_type.toLowerCase() === 'custom'
}

/** Epoch-seconds string -> ms, for sorting and dates. Null when unusable. */
export function updatedAtMs(e: Extractor): number | null {
  if (!e.update_tstamp) return null
  const n = Number(e.update_tstamp)
  return Number.isFinite(n) && n > 0 ? n * 1000 : null
}

/**
 * What `validation_msg` actually holds.
 *
 * Three real cases plus a fallback, all observed:
 * - 'Success' (or any plain text) -> text
 * - a JSON array of stream mappings, carrying the streams the extractor writes
 *   to and any newly discovered fields -> stream-mapping
 * - a JSON array containing `extractor-match-list`, listing OTHER extractors
 *   whose filters also matched the sample event -> extractor-conflict
 * - anything else that parses as JSON -> json, kept raw rather than guessed at
 *
 * Never render the raw string in a table cell: two of these are multi-kilobyte
 * JSON documents.
 */
export type ValidationDetail =
  | { kind: 'none' }
  | { kind: 'text'; text: string }
  | { kind: 'stream-mapping'; streams: string[]; newFields: string[] }
  | { kind: 'extractor-conflict'; extractors: string[] }
  | { kind: 'json'; raw: string }

export function parseValidationDetail(msg: string | null | undefined): ValidationDetail {
  if (msg === null || msg === undefined || msg === '') return { kind: 'none' }

  let parsed: unknown
  try {
    parsed = JSON.parse(msg)
  } catch {
    return { kind: 'text', text: msg }
  }
  // JSON.parse succeeds on bare scalars too; those are still just text.
  if (parsed === null) return { kind: 'none' }
  if (typeof parsed !== 'object') return { kind: 'text', text: msg }

  const items: unknown[] = Array.isArray(parsed) ? parsed : [parsed]
  const obj = (v: unknown): Record<string, unknown> =>
    typeof v === 'object' && v !== null ? (v as Record<string, unknown>) : {}
  const strings = (v: unknown): string[] =>
    Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []

  const conflicts = items.flatMap((it) => {
    const list = obj(it)['extractor-match-list']
    return Array.isArray(list)
      ? list
          .map((m) => obj(m)['extractor-name'])
          .filter((n): n is string => typeof n === 'string')
      : []
  })
  if (conflicts.length) return { kind: 'extractor-conflict', extractors: [...new Set(conflicts)] }

  const streams = items
    .map((it) => obj(it)['stream'])
    .filter((s): s is string => typeof s === 'string')
  const newFields = items.flatMap((it) => strings(obj(it)['new_fields']))
  if (streams.length || newFields.length) {
    return { kind: 'stream-mapping', streams: [...new Set(streams)], newFields: [...new Set(newFields)] }
  }

  return { kind: 'json', raw: msg }
}

/** One-line summary of a validation detail, safe for a table cell. */
export function summariseValidation(d: ValidationDetail): string {
  switch (d.kind) {
    case 'none': return '—'
    case 'text': return d.text
    case 'stream-mapping': {
      const parts: string[] = []
      if (d.streams.length) parts.push(`${d.streams.length} stream${d.streams.length > 1 ? 's' : ''}`)
      if (d.newFields.length) parts.push(`${d.newFields.length} new field${d.newFields.length > 1 ? 's' : ''}`)
      return parts.join(' · ') || 'stream mapping'
    }
    case 'extractor-conflict':
      return `${d.extractors.length} conflicting extractor${d.extractors.length > 1 ? 's' : ''}`
    case 'json':
      return 'details available'
  }
}

/** The custom twin id a native gets in a scope: `"730"` + `"gotham"` → `"730_gotham"`. */
export function twinIdFor(nativeId: string, scopeId: string): string {
  return `${nativeId.split('_')[0]}_${scopeId}`
}

/** The listed twin of a native in this scope, if any — cloning again is refused by the backend. */
export function twinOf(native: Extractor, scopeId: string, all: Extractor[]): Extractor | null {
  const id = twinIdFor(native.extractor_id, scopeId)
  return all.find((e) => e.extractor_id === id) ?? null
}
